/**
 * Place Overlay Header Component
 * Displays place name, back button, and rating
 * Follows Single Responsibility Principle
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { CloseButton } from '@/components/common/CloseButton';

export interface PlaceOverlayHeaderProps {
  placeName: string;
  rating?: string;
  onBack?: () => void;
  className?: string;
}

export const PlaceOverlayHeader: React.FC<PlaceOverlayHeaderProps> = ({
  placeName,
  rating,
  onBack,
  className,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? spacing[2] : 0,
      }}
      className={className}
    >
      {/* Left side: Back + Place Name */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing[2],
          flex: 1,
        }}
      >
        {onBack && (
          <CloseButton
            onClick={onBack}
            size={20}
            ariaLabel='Back'
            variant='default'
          />
        )}
        <h2
          style={{
            fontFamily: fontPrimary,
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
            color: '#000',
            margin: 0,
          }}
        >
          {placeName}
        </h2>
      </div>
      {/* Rating - Right side on web, below place name on mobile - Only show if rating exists */}
      {rating && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing[2],
            alignSelf: isMobile ? 'flex-start' : 'center',
            marginLeft: isMobile
              ? onBack
                ? `calc(${spacing[1]}px * 2 + 20px + ${spacing[2]}px)`
                : 0
              : 0,
          }}
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M9 1.5L11.3175 6.195L16.5 6.9525L12.75 10.605L13.635 15.765L9 13.3275L4.365 15.765L5.25 10.605L1.5 6.9525L6.6825 6.195L9 1.5Z'
              fill='#FFB800'
              stroke='#FFB800'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <span
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.semibold,
              color: typographyColors.primary,
            }}
          >
            {rating}
          </span>
        </div>
      )}
    </div>
  );
};
